<template>
  <section
    class="purchase-pod"
    :class="{
      'purchase-pod--collapsed': isGalleryOpen && isMqLarger,
      'purchase-pod--subscription': isSubscription,
    }"
  >
    <StickyMenu
      ref="stickyMenu"
      class="purchase-pod__content"
      :isActive="isVisible && isMqLarger"
      @mounted="onStickyMenuMounted"
    >
      <slot></slot>
      <Breadcrumbs v-if="!isSubscription">
        <template v-if="isGiftCard">
          <Breadcrumb
            :breadcrumbLink="''"
            :breadcrumbName="$t(`pdp.product_types.${productType}`)"
            :isSeparatorVisible="false"
            :isLink="false"
          />
        </template>
        <template v-else>
          <Breadcrumb
            v-if="!isGenderUnisex"
            :breadcrumbLink="buildExploreUrl(variant.gender)"
            :breadcrumbName="$t(`pdp.genders.${variant.gender}`)"
            :isSeparatorVisible="true"
          />
          <Breadcrumb
            :breadcrumbLink="buildExploreUrl(variant.gender, productType)"
            :breadcrumbName="$t(`pdp.product_types.${productType}`)"
            :isSeparatorVisible="!!productSubtype"
          />
          <Breadcrumb
            v-if="productSubtype"
            :breadcrumbLink="buildExploreUrl(variant.gender, productType, productSubtype)"
            :breadcrumbName="$t(`pdp.subtype.${productSubtype}`)"
            :isSeparatorVisible="false"
          />
        </template>
      </Breadcrumbs>
      <div v-if="!isSubscription" class="purchase-pod__info-container">
        <LabelBadge
          v-if="isMqLarger"
          :productLabels="product.labels"
          :productVariantLabels="variant.labels"
          :isLastChance="isLastChance"
          :isSoldOut="isSoldOut"
          :backgroundColor="BADGE_BACKGROUND_COLORS.lightgrey"
        />
        <div class="purchase-pod__title-container">
          <h1 class="purchase-pod__title">{{ product.name }}</h1>
          <template v-if="!isGiftCard">
            <div class="purchase-pod__price purchase-pod__price--mobile">
              {{ productPrice }}
            </div>
            <div class="purchase-pod__description">
              {{ longDescription }}
            </div>
          </template>
        </div>
        <div v-if="!isGiftCard" class="purchase-pod__price-container">
          <span class="purchase-pod__price">{{ productPrice }}</span>
          <span class="purchase-pod__color">{{ variant.color }}</span>
        </div>
        <div v-else class="purchase-pod__price-container">
          <span class="purchase-pod__giftcard-price">
            {{ $t('pdp.purchase_pod.gift_card_starting_from', { giftcardStartingPrice }) }}
          </span>
        </div>
      </div>
      <GenderSelector @switch-gender="handleGenderSwitch" class="purchase-pod__gender-selector" />
      <VariantSelector
        v-if="!isSubscription"
        class="purchase-pod__variant-selector"
        :class="{ 'purchase-pod__variant-selector--invisible': !isVariantSelectorVisible }"
        @select-variant="handleVariantSelection"
      />

      <Sizes
        class="size-selector"
        :variant="variant"
        :variantSkus="variantSkus"
        :resetSizeTimestamp="resetSizeTimestamp"
        :isVariantSock="isVariantSock"
        :isRaffle="isRaffle"
        :isGiftCard="isGiftCard"
        :isBackorderable="isBackorderable"
        :isDisabled="hasRaffleCountdownEnded"
        :sizeFinderButtonState="sizeFinderButtonState"
        :isSizeReset="selectedSku === UNSELECTED_SKU"
        :imageUrl="imageUrl"
        :productName="productName"
        :gender="variant.gender"
        :hasError="hasError"
        :slug="productGroupSlug"
        :hasSizeInfo="hasSizeInfo"
        @select-size="setSelectedSize"
      />

      <div v-if="!isGiftCard" class="purchase-pod__additional-links">
        <div v-if="isSizeGuideShown && !isGenderUnisex" class="purchase-pod__size-guide">
          <span
            ref="sizeGuideInfo"
            class="purchase-pod__size-guide-text"
            v-html="
              addLink(sizeFinderTranslatedString, 'javascript:;', `purchase-pod__size-guide-link`)
            "
          ></span>
        </div>
        <span
          v-if="isLowStock && !isRaffle"
          class="purchase-pod__low-stock-notification"
          :class="[{ 'purchase-pod__low-stock-notification--wrapped': isShoeSmall }]"
        >
          {{ $tPlural('pdp.purchase_pod.low_stock', selectedSkuObj.stock) }}
        </span>
      </div>

      <PurchasePodCountdown
        v-if="isRaffle"
        class="purchase-pod__countdown"
        :variant="VARIANTS.medium"
        :theme="THEMES.white"
        :endDate="raffleExpiresAt"
        :hasCountdownEnded="hasRaffleCountdownEnded"
        :productName="this.productName"
      />

      <FormTextArea
        v-if="isGiftCard"
        class="leave-review__field"
        :value.sync="giftCardMessage"
        :label="$t('pdp.purchase_pod.giftcard_textarea_label')"
        :placeholder="$t('pdp.purchase_pod.giftcard_textarea_placeholder')"
        :labelVariant="LABEL_VARIANTS.fixed"
        :labelStyle="LABEL_STYLES.bold"
        :isCharacterCountActive="true"
        :maxCharacters="MAX_GIFTCARD_CHARACTERS"
        :isRequired="false"
        :showIndicator="false"
        :validations="charactersLength"
        :showMessageOn="showMessageOn"
      />

      <ButtonContainer
        ref="addToCartButton"
        class="purchase-pod__button-container"
        :class="{
          'purchase-pod__button-container--with-padding': isRaffle,
        }"
        :buttonColour="currentButtonState.buttonColour"
        :buttonHeight="isMqLarger ? BUTTON_STYLES.height.large : BUTTON_STYLES.height.normal"
        :isInactive="currentButtonState.isInactive"
        buttonClass="button--no-border"
        @click="currentButtonState.onClick"
      >
        <div slot="button-content">
          <div class="purchase-pod__button-content" v-data-test-id="'purchase-pod-button'">
            <span
              v-show="!!currentButtonState.iconClass"
              class="purchase-pod__button-icon"
              :class="[currentButtonState.iconClass]"
            ></span>
            {{ currentButtonState.text }}
          </div>
        </div>
      </ButtonContainer>

      <p v-if="isGiftCard" class="purchase-pod__gift-card-delivery-info">
        {{ $t('pdp.purchase_pod.digital_delivery') }}
      </p>

      <div v-if="isRaffle" class="purchase-pod__terms">
        <p class="purchase-pod__terms-paragraph">
          {{ $t('pdp.purchase_pod.raffle_terms.paragraph_one') }}
        </p>

        <p class="purchase-pod__terms-paragraph">
          {{ $t('pdp.purchase_pod.raffle_terms.paragraph_two') }}
        </p>

        <a class="purchase-pod__terms-link" :href="raffleFAQUrl" target="_blank">{{
          $t('pdp.purchase_pod.faq')
        }}</a>
      </div>

      <div
        v-if="isDescriptionVisible"
        ref="shortProductDesc"
        class="purchase-pod__description-short"
      >
        <span class="purchase-pod__description-title">{{ $t('best_for') }} </span>
        {{ shortDescription }}
      </div>

      <Accordion v-if="!isSubscription" :isExclusiveOpen="!isMqLarger">
        <template v-if="isGiftCard">
          <AccordionItem
            id="how-it-works"
            :index="1"
            :label="$t('pdp.purchase_pod.how_it_works')"
            :isClosingOnClickOfContent="true"
            @height-change="onAccordionHeightChange"
            @height-change-end="onAccordionHeightChangeEnd"
          >
            <div class="purchase-pod__accordion-description">
              {{ $t('pdp.purchase_pod.how_it_works_gift_cards') }}
            </div>
            <div class="purchase-pod__accordion-description">
              <a :href="giftCardsFaqsUrl" class="purchase-pod__accordion-link">
                {{ $t('pdp.purchase_pod.faqs') }}
              </a>
            </div>
            <div class="purchase-pod__accordion-description">
              <a :href="termsUrl" class="purchase-pod__accordion-link">
                {{ $t('pdp.purchase_pod.terms') }}
              </a>
            </div>
          </AccordionItem>
        </template>
        <template v-else>
          <AccordionItem
            v-if="hasSizeInformation"
            id="size-fit"
            :index="1"
            :label="$t('pdp.purchase_pod.size_fit')"
            :isClosingOnClickOfContent="true"
            @height-change="onAccordionHeightChange"
            @height-change-end="onAccordionHeightChangeEnd"
          >
            <div v-if="variant.fit.sizeAndFit" class="purchase-pod__accordion-description">
              {{ variant.fit.sizeAndFit }}
            </div>
            <div v-if="variant.modelInformation.text" class="purchase-pod__accordion-description">
              {{ variant.modelInformation.text }}
            </div>
            <div v-if="variant.inseams" class="purchase-pod__accordion-description">
              <span class="purchase-pod__accordion-description-title">
                {{ $t('pdp.purchase_pod.inseams') }}:
              </span>
              <br />
              <div v-for="inseam in sortedInseams" :key="inseam.size">
                <span class="purchase-pod__accordion-inseams-size"> {{ inseam.size }}</span>
                <span>
                  - {{ displaySizeMetric(inseam.cm) }}cm /
                  {{ displaySizeMetric(inseam.inches) }}"</span
                >
              </div>
            </div>
            <a
              v-if="isSizeGuideShown && !isGenderUnisex"
              class="purchase-pod__accordion-link"
              target="_blank"
              @click.stop="openSizeGuide($event, 'accordion')"
              >{{ $t('pdp.purchase_pod.size_guide') }}</a
            >
          </AccordionItem>

          <AccordionItem
            v-if="hasMaterialsInformation"
            id="size-fit"
            :index="2"
            :label="$t('pdp.purchase_pod.materials')"
            :isClosingOnClickOfContent="true"
            @height-change="onAccordionHeightChange"
            @height-change-end="onAccordionHeightChangeEnd"
          >
            <div v-if="product.composition.composition" class="purchase-pod__accordion-description">
              <span class="purchase-pod__accordion-description-title">
                {{ $t('pdp.purchase_pod.composition') }}:
              </span>
              {{ product.composition.composition }}
            </div>
            <div
              v-if="product.careInstructions.content"
              class="purchase-pod__accordion-description"
              v-html="product.careInstructions.content"
            ></div>
          </AccordionItem>

          <AccordionItem
            v-if="hasSustainabilityInformation"
            id="sustainability"
            :index="6"
            :label="$t('pdp.purchase_pod.sustainability')"
            :isClosingOnClickOfContent="true"
            @height-change="onAccordionHeightChange"
            @height-change-end="onAccordionHeightChangeEnd"
          >
            <div
              class="purchase-pod__accordian-description"
              v-html="product.sustainability.content"
            ></div>
          </AccordionItem>

          <AccordionItem
            v-if="hasShippingInformation"
            id="shipping-returns"
            :index="4"
            :label="$t('pdp.purchase_pod.shipping_returns')"
            :isClosingOnClickOfContent="true"
            @height-change="onAccordionHeightChange"
            @height-change-end="onAccordionHeightChangeEnd"
          >
            <div class="purchase-pod__accordion-description">{{ shippingInformation }}</div>
            <div class="purchase-pod__accordion-description">{{ returnsInformation }}</div>

            <a class="purchase-pod__accordion-link" :href="returnPolicyUrl" target="_blank">{{
              $t('pdp.purchase_pod.return_policy')
            }}</a>
          </AccordionItem>

          <AccordionItem
            v-if="!isRaffle"
            id="reviews"
            :index="3"
            :link="'.reviews'"
            :label="$t('pdp.purchase_pod.reviews')"
          >
            <template v-slot:icon>
              <FiveStarRating
                class="purchase-pod__accordion-rating"
                :rating="reviewTotal > 0 ? reviewRatingAverage : 0"
                :isSmall="true"
              />
            </template>
          </AccordionItem>
        </template>
      </Accordion>
    </StickyMenu>
    <PurchaseBar
      :name="product.name"
      :colorName="variant.color"
      :productPrice="
        isGiftCard
          ? this.$t('pdp.purchase_pod.gift_card_starting_from', { giftcardStartingPrice })
          : productPrice
      "
      :isTopFixed="isMqLarger && isVerticallyFixed && !isGalleryOpen"
      :isBottomFixed="!isMqLarger && isVerticallyFixed && !isGalleryOpen"
      :isRightFixed="isGalleryOpen"
      class="purchase-pod__purchase-bar"
      :class="{ 'purchase-pod__purchase-bar--no-transition': hasNoPurchaseBarTransition }"
    />
  </section>
</template>

<script>
// TODO: Remove once DY-test ended
// https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8773197#/smarts/api/548692
import DynamicYieldService from '@/api/services/_shared/dynamic-yield-service.js'
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
import {
  BADGE_BACKGROUND_COLORS,
  FILTER_PAGE_PATH,
  SHIPPING_RETURNS_PATH,
  TERMS_AND_CONDITIONS,
  RAFFLE_FAQ_PATH,
  GIFT_CARDS_FAQS,
} from '@/config/constants'
import { formatVariantSkusForOrder } from '@/components/ProductPage/transformation-helpers.js'
import { GENDERS, PRODUCT_TYPES, isLowStock } from '@/stores/product'
import ROUTE_NAMES from '@/router/route-names.js'
import { createIntersectionObserver, observeIntersection } from '@/helpers/intersection-observer'
import { addLink } from '@/helpers/string'
import { formatToCurrency, formatToWholeNumber } from '@/helpers/currency-formatter'
import { getSlug } from '@/helpers/url.js'
import { determineIfLastChance, determineIfSoldOut } from '@/helpers/product-availabilty.js'
import updateCartMixin from '@/mixins/update-cart'
import ButtonContainer from '@/components/_shared/Form/ButtonContainer.vue'
import { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import StickyMenu from '@/components/_shared/StickyMenu/StickyMenu.vue'
import Accordion from '@/components/_shared/Accordion.vue'
import AccordionItem from '@/components/_shared/AccordionItem/AccordionItem.vue'
import FiveStarRating from '@/components/_shared/Form/FiveStarsRating/FiveStarsRating.vue'
import FormTextArea from '@/components/_shared/Form/Input/FormTextArea.vue'
import { LABEL_VARIANTS, LABEL_STYLES } from '@/components/_shared/Form/Input/props-input'
import { EVENT_TYPES } from '@/components/_shared/Form/Validation/FieldValidator.vue'
import { TYPES as VALIDATION_TYPES } from '@/components/_shared/Form/Validation/FieldValidation.vue'
import LabelBadge from '@/components/_shared/LabelBadge/LabelBadge.vue'
import Sizes, { UNSELECTED_SKU } from '@/components/_shared/Sizes/Sizes.vue'
import sizeFinderData from '@/components/_shared/SizeGuide/SizeFinder/size-finder-data.json'
import Breadcrumb from '@/components/_shared/Breadcrumbs/Breadcrumb.vue'
import Breadcrumbs from '@/components/_shared/Breadcrumbs/Breadcrumbs.vue'
import VariantSelector from './VariantSelector.vue'
import GenderSelector from './GenderSelector.vue'
import PurchaseBar from './PurchaseBar/PurchaseBar.vue'
import PurchasePodCountdown, { VARIANTS, THEMES } from './PurchasePodCountdown.vue'

const ERROR_DISPLAY_DURATION = 1800
const PURCHASE_BAR_TRANSITION_DURATION = 250
const MAX_GIFTCARD_CHARACTERS = 250

export default {
  name: 'PurchasePod',
  components: {
    Accordion,
    AccordionItem,
    Breadcrumb,
    Breadcrumbs,
    ButtonContainer,
    FiveStarRating,
    FormTextArea,
    GenderSelector,
    LabelBadge,
    PurchaseBar,
    Sizes,
    StickyMenu,
    VariantSelector,
    PurchasePodCountdown,
  },
  mixins: [updateCartMixin],

  props: {
    isSubscription: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      BADGE_BACKGROUND_COLORS,
      BUTTON_STYLES,
      selectedVariant: {},
      selectedSkuObj: {},
      selectedSku: UNSELECTED_SKU,
      resetSizeTimestamp: new Date(),
      hasError: false,
      isVariantSelectorVisible: true,
      isVerticallyFixed: false,
      hasNoPurchaseBarTransition: false,
      PRODUCT_TYPES,
      UNSELECTED_SKU,
      VARIANTS,
      THEMES,
      LABEL_VARIANTS,
      LABEL_STYLES,
      giftCardMessage: '',
      MAX_GIFTCARD_CHARACTERS,
      charactersLength: [
        {
          validator: value => value.length <= MAX_GIFTCARD_CHARACTERS,
          message: this.$t('errors.characters_limit_gift_card', { MAX_GIFTCARD_CHARACTERS }),
        },
      ],
      showMessageOn: {
        [EVENT_TYPES.blur]: [VALIDATION_TYPES.error, VALIDATION_TYPES.warning],
      },
      isDescriptionVisible: false,
    }
  },

  computed: {
    ...mapState('product', [
      'isGalleryOpen',
      'reviewRatingAverage',
      'isSizeInfoVisible',
      'reviewTotal',
    ]),
    ...mapState('store', ['currency', 'shippingInformation', 'returnsInformation']),
    ...mapState('page', ['locale']),

    ...mapGetters({
      isMqLarger: 'viewport/isMqLarger',
      isVisible: 'product/isVisible',
      product: 'product/product',
      productGroupSlug: 'product/productGroupSlug',
      productName: 'product/productName',
      getFullUrl: 'page/getFullUrl',
      getCustomerServiceUrl: 'page/getCustomerServiceUrl',
      variant: 'product/currentVariant',
      isRaffle: 'product/isRaffle',
      isGiftCard: 'product/isGiftCard',
      raffleExpiresAt: 'product/raffleExpiresAt',
      hasRaffleCountdownEnded: 'product/hasRaffleCountdownEnded',
      isGiftCardOrder: 'order/isGiftCardOrder',
    }),

    giftcardStartingPrice() {
      const amount = Number(this.variantSkus[0].price)
      const formattedPrice = formatToCurrency(amount, this.locale, this.currency)
      return formatToWholeNumber(formattedPrice)
    },

    sizeFinderTranslatedString() {
      return this.isShoeSmall && this.isSizeInfoVisible
        ? this.$t('pdp.purchase_pod.size_guide_info')
        : `{{ ${this.$t('pdp.purchase_pod.size_guide')} }}`
    },

    hasSizeSelected() {
      return this.selectedSku !== UNSELECTED_SKU
    },

    isGenderUnisex() {
      return this.variant.gender === GENDERS.unisex
    },

    isLowStock() {
      return isLowStock(this.selectedSkuObj?.stock)
    },

    isMobile() {
      return !this.isMqLarger
    },

    variantSkus() {
      return formatVariantSkusForOrder(this.variant.spreeProduct.spreeVariants || [])
    },

    productPrice() {
      const amount = Number(this.variant.spreeProduct.price)
      return formatToCurrency(amount, this.locale, this.currency)
    },

    isVariantSock() {
      return /sock/.test(this.productGroupSlug)
    },

    isBackorderable() {
      return this.variant.spreeProduct.isBackorderable
    },

    isPreorderable() {
      return this.variant.spreeProduct.isPreorderable
    },

    isLastChance() {
      return determineIfLastChance(
        this.variant.spreeProduct.spreeVariants || [],
        this.isBackorderable
      )
    },

    isSoldOut() {
      return determineIfSoldOut(
        this.variant.spreeProduct.spreeVariants || [],
        this.variant.spreeProduct.isRaffle,
        this.variant.spreeProduct.hasRaffleEnded
      )
    },

    isSizeGuideShown() {
      return this.productType === PRODUCT_TYPES.shoes || this.productType === PRODUCT_TYPES.apparel
    },

    imageUrl() {
      return this.variant.defaultImage.mediaUrl
    },

    productType() {
      return this.variant.spreeProduct.productType
    },

    productSubtype() {
      return this.product.productSubtype
    },

    hasSizeInformation() {
      return (
        this.variant.fit.sizeAndFit || this.variant.modelInformation.text || this.variant.inseams
      )
    },

    sortedInseams() {
      const inseams = this.variant?.inseams || []
      return inseams.sort((a, b) => a.cm - b.cm)
    },

    hasMaterialsInformation() {
      return this.product.composition.composition || this.product.careInstructions.content
    },

    hasSustainabilityInformation() {
      return !!this.product.sustainability?.content
    },

    hasShippingInformation() {
      return this.shippingInformation || this.returnsInformation
    },

    returnPolicyUrl() {
      return this.getCustomerServiceUrl(SHIPPING_RETURNS_PATH)
    },

    termsUrl() {
      return this.getCustomerServiceUrl(TERMS_AND_CONDITIONS)
    },

    raffleFAQUrl() {
      return this.getFullUrl(RAFFLE_FAQ_PATH)
    },

    giftCardsFaqsUrl() {
      return this.getFullUrl(GIFT_CARDS_FAQS)
    },

    currentButtonState() {
      if (this.hasRaffleCountdownEnded) {
        return {
          isInactive: true,
          onClick: () => {},
          text: this.$t('pdp.purchase_pod.raffle_countdown_ended.cta'),
          iconClass: '',
          buttonColour: BUTTON_STYLES.color.white,
        }
      } else if (!this.hasSizeSelected) {
        return {
          isInactive: true,
          onClick: this.showHasNoSelectionError,
          text: this.errorText,
          iconClass: '',
        }
      } else if (this.isRaffle) {
        return {
          isInactive: false,
          onClick: () => this.redirectToNewRaffleEntryPage(this.selectedSkuObj),
          text: this.$t('pdp.purchase_pod.raffle_countdown.cta'),
          iconClass: '',
          buttonColour: BUTTON_STYLES.color.white,
        }
      } else if (this.isPreorderable) {
        return {
          isInactive: !this.hasSizeSelected,
          onClick: () => this.updateCartWithContext(),
          text: this.$t('pdp.purchase_pod.preorder_cta'),
          iconClass: 'icon-plus',
          buttonColour: BUTTON_STYLES.color.storm,
        }
      } else if (this.isSubscription) {
        return {
          isInactive: !this.hasSizeSelected,
          onClick: () => this.updateCartWithContext(),
          text: this.$t('pdp.purchase_pod.subscription.cta', { price: this.productPrice }),
          iconClass: '',
          buttonColour: BUTTON_STYLES.color.black,
        }
      } else if (this.isGiftCard) {
        return {
          isInactive: !this.hasSizeSelected || !this.isGiftCardMsgValid,
          onClick: () => this.addGiftCardToCart(),
          text: this.$t('pdp.purchase_pod.cta'),
          iconClass: '',
          buttonColour: BUTTON_STYLES.color.storm,
        }
      } else {
        return {
          isInactive: !this.hasSizeSelected,
          onClick: () => this.updateCartWithContext(),
          text: this.$t('pdp.purchase_pod.cta'),
          iconClass: 'icon-plus',
          buttonColour: BUTTON_STYLES.color.storm,
        }
      }
    },

    sizeFinderButtonState() {
      if (this.hasRaffleCountdownEnded) {
        return {
          isInactive: true,
          onClick: this.hideSizeGuideOverlay,
          text: this.$t('pdp.purchase_pod.raffle_countdown_ended.cta'),
        }
      } else if (this.isRaffle) {
        return {
          isInactive: false,
          onClick: ($event, selectedSkuObj) =>
            this.handleSizeFinderClick($event, selectedSkuObj, this.redirectToNewRaffleEntryPage),
          text: this.$t('pdp.purchase_pod.raffle_countdown.cta'),
        }
      } else {
        return {
          isInactive: false,
          onClick: ($event, selectedSkuObj) =>
            this.handleSizeFinderClick($event, selectedSkuObj, this.updateCart),
          text: this.$t('size_guide.size_finder.add'),
        }
      }
    },

    isShoeSmall() {
      const slug = getSlug(this.variant.spreeProduct.productUrl, 'products')
      return Object.values(sizeFinderData.deltas).some(products => products.includes(slug))
    },

    longDescription() {
      return this.variant.description || this.product.description
    },

    shortDescription() {
      return this.product.shortDescription || ''
    },

    hasSizeInfo() {
      return this.productType === PRODUCT_TYPES.shoes
    },

    errorText() {
      return !this.isGiftCard
        ? this.$t('pdp.purchase_pod.select_size')
        : this.$t('pdp.purchase_pod.select_price')
    },

    isGiftCardMsgValid() {
      return this.giftCardMessage.length <= MAX_GIFTCARD_CHARACTERS
    },
  },

  watch: {
    variant() {
      this.$refs.stickyMenu.scrollToTop()
    },

    async $route(to) {
      if (!to.params.isVariantClick) return

      // Prevent scrolling to top of page on variant change
      const previousScrollY = window.pageYOffset
      await this.$nextTick()
      window.scrollTo(0, previousScrollY)
    },

    isGalleryOpen() {
      if (!this.isGalleryOpen) {
        this.hasNoPurchaseBarTransition = true
        setTimeout(() => {
          this.hasNoPurchaseBarTransition = false
        }, PURCHASE_BAR_TRANSITION_DURATION)
      }
    },
  },

  created() {
    if (this.isMobile) this.startExperiment()
  },

  methods: {
    ...mapMutations({
      setStickyMenuMounted: 'product/setStickyMenuMounted',
    }),

    ...mapActions({
      showSizeGuideOverlay: 'overlay/showSizeGuideOverlay',
      hideSizeGuideOverlay: 'overlay/hideSizeGuideOverlay',
      showOutOfStockOverlay: 'overlay/showOutOfStockOverlay',
    }),

    async startExperiment() {
      await this.fetchCampaignData()
      this.observeProductDescriptionIntersection()
    },

    // TODO: Remove once DY-test ended
    // https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8773197#/smarts/api/548692
    async fetchCampaignData() {
      const data = await DynamicYieldService.fetchCampaign(['pdp-product-description'])
      this.isDescriptionVisible = data.campaigns[0].variation.isDescriptionVisible
    },

    observeProductDescriptionIntersection() {
      if (this.isDescriptionVisible) {
        const onIntersection = () => this.trackCampaignInView()
        observeIntersection(this.$refs.shortProductDesc, onIntersection, { threshold: 1 })
      }
    },

    trackCampaignInView() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignView,
        data: { identifier: 'pdp-description' },
      })
    },

    addLink,

    openSizeGuide($event, eventValue) {
      this.showSizeGuideOverlay({
        fit: this.variant.fit,
        gender: this.variant.gender,
        productName: this.variant.productName || this.variant.spreeProduct.groupName,
        productType: this.variant.spreeProduct.productType,
        productUrl: this.variant.spreeProduct.productUrl,
        variantSkus: this.variantSkus,
        sizeFinderButtonState: this.sizeFinderButtonState,
      })

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.sizeGuide,
        details: { action: 'open_overlay', label: eventValue },
      })

      if (this.productType === 'shoes') {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.sizeGuide,
          details: { action: 'open_size_finder', label: 'purchase pod' },
        })
      }
    },

    onStickyMenuMounted() {
      this.setStickyMenuMounted(true)
      this.observeAddToCartButtonIntersection()
      if (this.isSizeGuideShown) this.addSizeGuideListener()
    },

    observeAddToCartButtonIntersection() {
      if (!window.IntersectionObserver) return

      createIntersectionObserver(
        this.$refs.addToCartButton.$el,
        entry => {
          if (entry.target === this.$refs.addToCartButton?.$el) {
            this.isVerticallyFixed = entry.intersectionRatio !== 1 && entry.boundingClientRect.y < 0
          }
        },
        { threshold: 1 }
      )
    },

    setSelectedSize(selectedSkuObj) {
      this.selectedSku =
        selectedSkuObj?.sku === UNSELECTED_SKU
          ? UNSELECTED_SKU
          : selectedSkuObj?.sku || this.selectedSku
      this.selectedSkuObj = this.variantSkus.find(sku => sku.sku === this.selectedSku && sku.stock)
      if (!this.selectedSkuObj) this.selectedSku = UNSELECTED_SKU
    },

    handleVariantSelection() {
      this.setSelectedSize()
      this.resetSizeTimestamp = new Date()
      this.selectedVariant = {}
    },

    handleGenderSwitch() {
      this.setSelectedSize({})
      this.isVariantSelectorVisible = false
      this.$nextTick(() => (this.isVariantSelectorVisible = true))
    },

    showHasNoSelectionError() {
      this.hasError = true

      setTimeout(() => {
        this.hasError = false
      }, ERROR_DISPLAY_DURATION)
    },

    onAccordionHeightChange(additionalHeight) {
      this.$refs.stickyMenu.beforeHeightChange(additionalHeight, true)
    },

    onAccordionHeightChangeEnd() {
      this.$refs.stickyMenu.afterHeightChange()
    },

    buildExploreUrl(...filters) {
      return this.getFullUrl(`${FILTER_PAGE_PATH}/${filters.join('/')}`)
    },

    redirectToNewRaffleEntryPage({ productVariantId }) {
      const newRaffleEntryUrl = this.getFullUrl(
        `${ROUTE_NAMES.raffleEntryCreate}/${productVariantId}`
      )
      window.location.assign(newRaffleEntryUrl)
    },

    handleSizeFinderClick($event, selectedSkuObj, action) {
      this.hideSizeGuideOverlay()
      if (selectedSkuObj.stock) {
        action(selectedSkuObj)
      } else {
        const { productName, color, gender } = this.variant
        this.showOutOfStockOverlay({
          color,
          gender,
          productName,
          imageUrl: this.imageUrl,
          isBackorderable: this.isBackorderable,
          sku: selectedSkuObj.sku,
          slug: this.productGroupSlug,
          isRaffle: this.isRaffle,
        })
      }
    },

    addSizeGuideListener() {
      this.$refs.sizeGuideInfo?.firstElementChild.addEventListener('click', event => {
        event.preventDefault()
        this.openSizeGuide(event, 'purchase pod')
      })
    },

    displaySizeMetric(sizeMetric) {
      return sizeMetric.toLocaleString(this.locale)
    },

    addGiftCardToCart() {
      if (!this.isGiftCardMsgValid) return
      const { giftCardMessage } = this
      this.updateCartWithContext({ isGiftCardOrder: true, giftCardMessage })
      this.giftCardMessage = ''
    },

    updateCartWithContext(options = {}) {
      this.updateCart(this.selectedSkuObj, {
        context: this.$CONTEXTS.purchasePod,
        ...options,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './PurchasePod';
</style>

<i18n src="@/locales/purchase_pod.json">
</i18n>
